// components/TrailingStopCalculator.jsx
import React, { useState } from 'react';
import '../../styles/TrailingStopCalculator.css';

export const TrailingStopCalculator = ({ onCalculate, portfolioId }) => {
  const [formData, setFormData] = useState({
    entryPrice: 150.00,
    currentPrice: 165.00,
    atr: 3.50,
    volatility: 0.18,
    riskTolerance: 'medium',
    trailingType: 'atr',
    atrMultiplier: 2,
    percentage: 10,
    movingAverage: 20
  });

  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleCalculate = async () => {
    const response = await onCalculate(formData);
    
    // Calculate trailing stop levels
    const priceChange = ((formData.currentPrice - formData.entryPrice) / formData.entryPrice * 100);
    let stopPrice, stopDistance, stopPercentage;
    
    if (formData.trailingType === 'atr') {
      stopDistance = formData.atr * formData.atrMultiplier;
      stopPrice = formData.currentPrice - stopDistance;
      stopPercentage = (stopDistance / formData.currentPrice * 100);
    } else if (formData.trailingType === 'percentage') {
      stopPercentage = formData.percentage;
      stopPrice = formData.currentPrice * (1 - formData.percentage / 100);
      stopDistance = formData.currentPrice - stopPrice;
    } else {
      // Moving average
      stopPrice = formData.currentPrice * (1 - (formData.movingAverage / 100));
      stopDistance = formData.currentPrice - stopPrice;
      stopPercentage = (stopDistance / formData.currentPrice * 100);
    }
    
    const maxLoss = stopPrice - formData.entryPrice;
    const unrealizedGain = formData.currentPrice - formData.entryPrice;
    const protectedGain = formData.currentPrice - stopPrice;
    const riskReward = Math.abs(unrealizedGain / maxLoss);

    setResult({
      ...response,
      timestamp: new Date().toLocaleTimeString(),
      stopPrice,
      stopDistance,
      stopPercentage,
      maxLoss,
      unrealizedGain,
      protectedGain,
      riskReward,
      priceChange,
      recommendation: stopPercentage > 15 ? 'Consider tightening stop' : 'Stop level appropriate',
      breakoutLevel: formData.currentPrice + (stopDistance * 1.5)
    });
  };

  const riskToleranceOptions = [
    { value: 'low', label: 'Low Risk', description: 'Tight stops, frequent adjustments' },
    { value: 'medium', label: 'Medium Risk', description: 'Balanced approach' },
    { value: 'high', label: 'High Risk', description: 'Wide stops, less frequent adjustments' }
  ];

  const trailingTypes = [
    { value: 'atr', label: 'ATR-based', description: 'Based on Average True Range' },
    { value: 'percentage', label: 'Percentage', description: 'Fixed percentage from current price' },
    { value: 'ma', label: 'Moving Average', description: 'Based on moving average distance' }
  ];

  return (
    <div className="panel trailing-stop-calculator">
      <div className="panel-header">
        <h2>Trailing Stop Calculator</h2>
        <span className="api-endpoint">GET /rms/risk/trailing-stop</span>
      </div>

      <div className="panel-content">
        <div className="calculator-grid">
          <div className="input-section">
            <h3>Position Details</h3>
            
            <div className="form-group">
              <label htmlFor="entryPrice">Entry Price ($)</label>
              <input
                type="number"
                id="entryPrice"
                name="entryPrice"
                step="0.01"
                value={formData.entryPrice}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentPrice">Current Price ($)</label>
              <input
                type="number"
                id="currentPrice"
                name="currentPrice"
                step="0.01"
                value={formData.currentPrice}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="atr">ATR (Average True Range)</label>
              <input
                type="number"
                id="atr"
                name="atr"
                step="0.01"
                value={formData.atr}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="volatility">Volatility (%)</label>
              <input
                type="number"
                id="volatility"
                name="volatility"
                step="0.01"
                value={formData.volatility}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="settings-section">
            <h3>Stop Settings</h3>

            <div className="setting-group">
              <label>Risk Tolerance</label>
              <div className="option-buttons">
                {riskToleranceOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`option-btn ${formData.riskTolerance === option.value ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, riskTolerance: option.value }))}
                    title={option.description}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="setting-group">
              <label>Trailing Stop Type</label>
              <div className="option-buttons vertical">
                {trailingTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`option-btn ${formData.trailingType === type.value ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, trailingType: type.value }))}
                    title={type.description}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {formData.trailingType === 'atr' && (
              <div className="setting-group">
                <label htmlFor="atrMultiplier">ATR Multiplier: {formData.atrMultiplier}x</label>
                <input
                  type="range"
                  id="atrMultiplier"
                  name="atrMultiplier"
                  min="1"
                  max="4"
                  step="0.5"
                  value={formData.atrMultiplier}
                  onChange={handleChange}
                />
              </div>
            )}

            {formData.trailingType === 'percentage' && (
              <div className="setting-group">
                <label htmlFor="percentage">Stop Percentage: {formData.percentage}%</label>
                <input
                  type="range"
                  id="percentage"
                  name="percentage"
                  min="5"
                  max="25"
                  step="1"
                  value={formData.percentage}
                  onChange={handleChange}
                />
              </div>
            )}

            {formData.trailingType === 'ma' && (
              <div className="setting-group">
                <label htmlFor="movingAverage">MA Distance: {formData.movingAverage}%</label>
                <input
                  type="range"
                  id="movingAverage"
                  name="movingAverage"
                  min="5"
                  max="30"
                  step="1"
                  value={formData.movingAverage}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        </div>

        <div className="price-visualization">
          <h3>Price Movement</h3>
          <div className="price-chart">
            <div className="chart-lines">
              <div className="price-line entry" style={{ bottom: '30%' }}>
                <span className="line-label">Entry: ${formData.entryPrice}</span>
              </div>
              <div className="price-line current" style={{ bottom: '60%' }}>
                <span className="line-label">Current: ${formData.currentPrice}</span>
              </div>
              {result && (
                <div className="price-line stop" style={{ bottom: '20%' }}>
                  <span className="line-label">Stop: ${result.stopPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="chart-bars">
              <div className="price-change-bar">
                <div className="bar-label">Entry → Current</div>
                <div className="bar-container">
                  <div 
                    className="bar-fill positive"
                    style={{ width: `${Math.min(((formData.currentPrice - formData.entryPrice) / formData.entryPrice * 100) * 2, 100)}%` }}
                  />
                </div>
                <div className="bar-value">
                  +{((formData.currentPrice - formData.entryPrice) / formData.entryPrice * 100).toFixed(1)}%
                </div>
              </div>
              {result && (
                <div className="price-change-bar">
                  <div className="bar-label">Protected Gain</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill warning"
                      style={{ width: `${Math.min((result.protectedGain / formData.entryPrice * 100) * 2, 100)}%` }}
                    />
                  </div>
                  <div className="bar-value">
                    +{(result.protectedGain / formData.entryPrice * 100).toFixed(1)}%
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="calculate-section">
          <button 
            className="btn btn-primary calculate-btn"
            onClick={handleCalculate}
          >
            🎯 Calculate Trailing Stop
          </button>
        </div>

        {result && (
          <div className="stop-results">
            <div className="results-header">
              <h3>Trailing Stop Results</h3>
              <span className="timestamp">{result.timestamp}</span>
            </div>

            <div className="stop-metrics">
              <div className="stop-metric-card main">
                <div className="stop-metric-label">Recommended Stop Price</div>
                <div className="stop-metric-value">
                  ${result.stopPrice.toFixed(2)}
                </div>
                <div className="stop-metric-subtext">
                  {result.stopPercentage.toFixed(1)}% below current price
                </div>
              </div>

              <div className="stop-metric-card">
                <div className="stop-metric-label">Stop Distance</div>
                <div className="stop-metric-value">
                  ${result.stopDistance.toFixed(2)}
                </div>
                <div className="stop-metric-subtext">
                  Based on {formData.trailingType === 'atr' ? `${formData.atrMultiplier}x ATR` : 
                           formData.trailingType === 'percentage' ? `${formData.percentage}%` : 
                           `${formData.movingAverage}% from MA`}
                </div>
              </div>

              <div className="stop-metric-card">
                <div className="stop-metric-label">Protected Gain</div>
                <div className="stop-metric-value positive">
                  ${result.protectedGain.toFixed(2)}
                </div>
                <div className="stop-metric-subtext">
                  {(result.protectedGain / formData.entryPrice * 100).toFixed(1)}% from entry
                </div>
              </div>
            </div>

            <div className="risk-analysis">
              <h4>Risk Analysis</h4>
              <div className="analysis-grid">
                <div className="analysis-card">
                  <div className="analysis-label">Maximum Loss</div>
                  <div className="analysis-value danger">
                    ${Math.abs(result.maxLoss).toFixed(2)}
                  </div>
                  <div className="analysis-change">
                    {(result.maxLoss / formData.entryPrice * 100).toFixed(1)}% from entry
                  </div>
                </div>
                
                <div className="analysis-card">
                  <div className="analysis-label">Unrealized Gain</div>
                  <div className="analysis-value positive">
                    ${result.unrealizedGain.toFixed(2)}
                  </div>
                  <div className="analysis-change">
                    +{(result.unrealizedGain / formData.entryPrice * 100).toFixed(1)}%
                  </div>
                </div>
                
                <div className="analysis-card">
                  <div className="analysis-label">Risk/Reward Ratio</div>
                  <div className="analysis-value">
                    1:{result.riskReward.toFixed(2)}
                  </div>
                  <div className="analysis-change">
                    {result.riskReward > 3 ? 'Excellent' : result.riskReward > 2 ? 'Good' : 'Poor'}
                  </div>
                </div>
                
                <div className="analysis-card">
                  <div className="analysis-label">Breakout Level</div>
                  <div className="analysis-value">
                    ${result.breakoutLevel.toFixed(2)}
                  </div>
                  <div className="analysis-change">
                    +{((result.breakoutLevel - formData.currentPrice) / formData.currentPrice * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            <div className="recommendations">
              <h4>Recommendations</h4>
              <div className="recommendation-card">
                <div className="rec-icon">💡</div>
                <div className="rec-content">
                  <div className="rec-title">{result.recommendation}</div>
                  <div className="rec-details">
                    {result.stopPercentage > 15 
                      ? 'Consider reducing stop distance to protect more gains. A 10-12% stop may be more appropriate for current volatility.'
                      : 'Current stop level provides adequate protection while allowing for normal price fluctuations.'
                    }
                  </div>
                </div>
              </div>
              
              <div className="action-steps">
                <h5>Next Steps:</h5>
                <ol>
                  <li>Set stop order at ${result.stopPrice.toFixed(2)}</li>
                  <li>Monitor price action and adjust stop weekly</li>
                  <li>Consider moving stop to breakeven at ${formData.entryPrice.toFixed(2)}</li>
                  <li>Review stop level if volatility changes significantly</li>
                </ol>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-primary">
                🔔 Set Stop Alert
              </button>
              <button className="btn btn-secondary">
                📋 Copy Stop Levels
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};